import {
  BagModalButtons,
  BagModalProduct,
  BagModalShell,
} from "@/components/tactsuit/components/buy-tactsuit/bag-modal-shell";
import { GePrice } from "@/v2-components/GePrice";
import { useCartWithProducts } from "@/store/useCartWithProducts";
import { useProductPrice } from "@/hooks/useProductPrice";
import { useCurrency } from "@/hooks/useCurrency";
import { useLocale } from "@/store/locale-store";
import CountryUtils from "@/utils/CountryUtils";
import { useTrans } from "@/utils/useTrans";
import { formatMoney } from "@/utils/CommonUtils";

/**
 * 번들 병합 모달 (#5356) — 담은 것이 **번들로 묶여 더 싸졌다**를 알린다.
 *
 * 시안: 데스크톱 [29457:37795](EN) / 29444:145215(KR) ·
 *       모바일 29458:37799(EN) / 29444:145236(KR) = **바텀 드로어**.
 *
 * ## 종전 `BundledDialog` 와 무엇이 다른가
 *
 * 종전은 51줄이었고 내용이 제목 + "discount applied!" + `OK` 하나였다. 시안은 담기 확인
 * 모달(#5355)과 **같은 껍데기**에 할인 표기가 얹힌 형태다:
 *
 *   · 제품 카드 — 줄긋기 정가 + **절약액 줄**(`You've saved $000!`, `Blue/60`)
 *   · 요약 — `justify-between` 2열(왼쪽 라벨 / 오른쪽 값). #5355 는 한 줄이라 **다르다**
 *   · 버튼 — `Keep Shopping` / `View Bag` (종전 `OK` 하나 → 둘)
 *
 * ## 절약액 = 「개별 구매 대비」
 *
 * 시안 문구가 KR 에서 명시적이다 — `개별 구매 대비 ₩00,000 절약`. 즉 번들가와 **구성품을
 * 따로 살 때의 합계** 차이다. 그 값은 이미 카탈로그에 있다: 번들 제품의
 * `originalAmount − discountAmount`(= 실판매가)와 `originalAmount` 의 차이를
 * `useProductPrice` 가 `displayDiscountAmount` 로 준다(`cart-item` 과 같은 파생).
 *
 * ⚠️ **`displayPrice`(MSRP)로 계산하지 않는다** — 그러면 정가 기준이 되어 실제 절약액과
 * 어긋난다(`docs/order-item-pricing.md`). 이 모달은 카트가 쓰는 것과 **같은 훅**을 쓴다.
 *
 * ## 요약줄의 배송 행은 무료가 확정될 때만
 *
 * #5355 와 같은 판정이다 — `ShippingApi.calculateShippingPriceBySku` 가 `KR` 을 네트워크
 * 없이 `price: 0` 으로 단락시키므로 KR 만 조회 없이 확정된다. 비-KR 은 그리지 않는다.
 */
export function BundleMergedDialog({
  open,
  onClose,
  bundleSlug,
  onViewCart,
}: {
  open: boolean;
  onClose: () => void;
  /**
   * 병합 결과로 담긴 **번들 제품의 slug**. 카트 전체가 아니라 그 항목을 보여준다.
   * `useBundledStore` 의 `bundle` 값이 이것이다.
   */
  bundleSlug: string | null;
  onViewCart: () => void;
}) {
  const { t } = useTrans(["gnb"]);
  const { items } = useCartWithProducts();
  const bundle = items.find((item) => item.slug === bundleSlug);

  return (
    <BagModalShell
      open={open}
      onClose={onClose}
      // 시안: `FPS Bundle applied` / `FPS Bundle 로 묶어 더 저렴해졌습니다`
      title={t("{{name}} applied", { name: bundle?.name ?? bundleSlug ?? "" })}
    >
      <BundleMergedBody
        bundleSlug={bundleSlug}
        onClose={onClose}
        onViewCart={onViewCart}
      />
    </BagModalShell>
  );
}

function BundleMergedBody({
  bundleSlug,
  onClose,
  onViewCart,
}: {
  bundleSlug: string | null;
  onClose: () => void;
  onViewCart: () => void;
}) {
  const { t } = useTrans(["gnb"]);
  const currency = useCurrency();
  const { countryCode } = useLocale();
  const {
    items,
    totalCount,
    displayTotalOriginalPrice,
    getProductPrice,
    isPending,
  } = useCartWithProducts();

  const bundle = items.find((item) => item.slug === bundleSlug);
  const price = bundle ? getProductPrice(bundle) : undefined;
  const { displaySalePrice, displayOriginalPrice, displayDiscountAmount } =
    useProductPrice(price);

  return (
    <>
      {/* Contents — 시안 `gap-20` */}
      <div className="flex flex-col gap-5">
        {bundle && (
          /* 썸네일은 **두 표면 모두 64px** 이다(#5355 는 데스크톱 80 — 시안이 다르다).
             제품명은 semibold `tracking-[0.25px]`(#5355 는 medium `tracking-[0.1px]`). */
          <BagModalProduct
            thumbnail={
              bundle.skuOptions?.find((opt) => opt.sku === bundle.sku)
                ?.thumbnail || bundle.thumbnail
            }
            name={bundle.name}
            options={bundle.options}
            nameClassName="font-600 tracking-[0.25px]"
          >
            {!!price && (
              <>
                {/* 가격 행 — 시안 `gap-8`: 판매가(semibold 14) + 정가(줄긋기 12).
                    `basketAttr` 는 `cart-item` 과 같은 값을 쓴다 — Global-E 스펙(2026-06-16)이
                    현재가를 `producttotalprice`, 원가를 `productsubtotalprice` 로 읽으므로
                    두 곳이 갈리면 GE 가 카트와 이 모달에서 다른 값을 집계한다. */}
                <div className="flex flex-wrap items-center gap-2">
                  <GePrice
                    amount={displaySalePrice * bundle.quantity}
                    currency={currency}
                    countryCode={countryCode}
                    basketAttr="producttotalprice"
                    className="text-base font-600 tracking-[0.25px] text-coolgray-90"
                  />
                  {!!displayDiscountAmount && (
                    <GePrice
                      amount={displayOriginalPrice * bundle.quantity}
                      currency={currency}
                      countryCode={countryCode}
                      basketAttr="productsubtotalprice"
                      className="text-sm tracking-[0.4px] text-coolgray-60 line-through"
                    />
                  )}
                </div>
                {/* ★절약액 — 이 모달의 존재 이유다. **할인이 있을 때만** 그린다:
                    번들이 항상 할인가라는 보장이 없고(admin 등록값), 0 을 "0 절약" 으로
                    적으면 거짓 약속이 된다. */}
                {!!displayDiscountAmount && (
                  /* ★`data-gem-ignore` 필수 — `GePrice` 를 안 거치는 **plain 가격 텍스트**다.
                     GE 는 우리 마커와 **무관하게** 페이지의 모든 가격 텍스트를 자체 검출해
                     현지통화로 in-place 변환한다(`docs/global-e-storefront.md` §4.11, dev DOM
                     실측 2026-07-14). 그래서 마커를 안 붙이는 것으로는 **못 막는다** —
                     같은 클래스가 `price-summary-ui` 배송비 span 에서 이미 한 번 누락됐다
                     (#5013). flag OFF 인 prod 에선 무해하지만 flag 는 배포 없이 켜진다. */
                  <p
                    data-gem-ignore
                    className="text-sm font-600 tracking-[0.4px] text-blue-60"
                  >
                    {t("You've saved {{amount}}!", {
                      amount: formatMoney({
                        amount: displayDiscountAmount * bundle.quantity,
                        currency,
                        countryCode,
                      }),
                    })}
                  </p>
                )}
              </>
            )}
          </BagModalProduct>
        )}

        {/* Summary — 시안은 **`justify-between` 2열**이다(#5355 의 한 줄과 다르다).
            왼쪽 라벨 `Cool Gray/60` regular · 오른쪽 값 `Cool Gray/90` semibold. */}
        <div className="flex flex-col gap-1 text-base tracking-[0.25px]">
          <div className="flex items-center justify-between">
            <span className="text-coolgray-60">
              {t("{{count}} items in bag", { count: totalCount })}
            </span>
            <span className="flex items-center gap-1 font-600 text-coolgray-90">
              {t("Subtotal")}
              {isPending ? (
                <span>—</span>
              ) : (
                <GePrice
                  amount={displayTotalOriginalPrice}
                  currency={currency}
                  countryCode={countryCode}
                />
              )}
            </span>
          </div>
          {/* 배송 행은 **무료가 확정될 때만** — #5355 와 같은 판정(`ShippingApi:30` 가 KR 을
              네트워크 없이 0 으로 단락). 비-KR 은 조회해야 알 수 있어 그리지 않는다. */}
          {CountryUtils.isKR(countryCode) && (
            <div className="flex items-center justify-between">
              <span className="text-coolgray-60">{t("Shipping")}</span>
              <span className="font-600 text-coolgray-90">
                {t("Free Shipping")}
              </span>
            </div>
          )}
        </div>
      </div>

      <BagModalButtons onKeepShopping={onClose} onViewCart={onViewCart} />
    </>
  );
}

/*
 * 절약액은 `GePrice` 로 감쌀 수 없다 — 문장 안에 들어가므로(`You've saved {{amount}}!`)
 * 요소로 끼울 수 없다. 그래서 **금액 포맷은 리포 정본(`formatMoney`)** 을 쓰고, **변환
 * 차단은 `data-gem-ignore`** 로 명시한다(위 주석).
 *
 * ⚠️ `Intl.NumberFormat(undefined, …)` 을 직접 쓰면 안 된다 — 첫 인자 `undefined` 는
 * **브라우저 로케일**이라 앱 로케일과 갈린다(kr 사용자 + en-US 브라우저에서 구분자·기호
 * 배치가 달라져 같은 줄의 `GePrice` 금액들과 표기가 어긋난다). `formatMoney` 는 통화에서
 * 언어를 파생시킨다(`convertCurrencyToLanguage`) — 그래서 `GePrice` 와 같은 표기가 된다.
 */
